import { z } from 'zod';
import { defineRoute } from '../core/contract';
import { BadgeEarnedZ } from '../schemas/badge';
import { IdZ, qInt, TeamZ, TsZ } from '../schemas/common';
import { FeedItemZ } from '../schemas/feed';
import { GameModeZ } from '../schemas/game';
import { DeltaSourceZ, RatingCardZ, RatingDeltaZ, RatingHistoryZ } from '../schemas/rating';

/**
 * Owner: stats. One engine (0 to 99), tiers, Elo, badges, records, leaderboards,
 * head-to-head, feed (R26: everything under /api/stats/*).
 */

/** A per-game or percentage stat with its honesty sample (stats.md section 3). */
export const StatValueZ = z.object({
  value: z.number().nullable(),
  /** Games (or attempts) the value is computed from. */
  sample: z.number().int().nonnegative(),
  /** Games in scope. */
  of: z.number().int().nonnegative(),
});

export const WindowZ = z.enum(['all', '30d']);

export const PlayerLineZ = z.object({
  playerId: IdZ,
  team: TeamZ,
  pts: z.number().int().nullable(),
  ptsNorm: z.number().int().nullable(),
  fgm: z.number().int(),
  fga: z.number().int().nullable(),
  rimM: z.number().int(),
  rimA: z.number().int().nullable(),
  midM: z.number().int(),
  midA: z.number().int().nullable(),
  twoM: z.number().int(),
  twoA: z.number().int().nullable(),
  tpm: z.number().int(),
  tpa: z.number().int().nullable(),
  ftm: z.number().int(),
  fta: z.number().int().nullable(),
  oreb: z.number().int().nullable(),
  dreb: z.number().int().nullable(),
  ast: z.number().int().nullable(),
  stl: z.number().int().nullable(),
  blk: z.number().int().nullable(),
  tov: z.number().int().nullable(),
  pf: z.number().int().nullable(),
  plusMinus: z.number().int().nullable(),
  gameWinner: z.boolean(),
  won: z.boolean().nullable(),
  /** Player of the Game score (gameplay.md section 3.7). */
  potg: z.number().nullable(),
});
export type PlayerLine = z.infer<typeof PlayerLineZ>;

export const BoxScoreZ = z.object({
  gameId: IdZ,
  revision: z.number().int(),
  scoreA: z.number().int(),
  scoreB: z.number().int(),
  winner: TeamZ.nullable(),
  coverage: z.number().int(),
  lines: z.array(PlayerLineZ),
  playerOfTheGame: IdZ.nullable(),
});
export type BoxScore = z.infer<typeof BoxScoreZ>;

export const PlayerStatsZ = z.object({
  playerId: IdZ,
  window: WindowZ,
  gp: z.number().int(),
  w: z.number().int(),
  l: z.number().int(),
  elo1v1: z.number(),
  eloTeam: z.number(),
  stats: z.record(z.string(), StatValueZ),
  streak: z.object({ current: z.number().int(), best: z.number().int() }),
  byMode: z.array(z.object({ mode: GameModeZ, gp: z.number().int(), w: z.number().int(), l: z.number().int() })),
});
export type PlayerStats = z.infer<typeof PlayerStatsZ>;

export const LeaderboardZ = z.object({
  stat: z.string(),
  window: WindowZ,
  rows: z.array(
    z.object({
      rank: z.number().int().positive(),
      playerId: IdZ,
      value: z.number(),
      sample: z.number().int(),
    }),
  ),
  qualifier: z.string().nullable(),
});

export const RecordBookZ = z.object({
  records: z.array(
    z.object({
      key: z.string(),
      scope: z.string(),
      label: z.string(),
      value: z.number(),
      holders: z.array(z.object({ playerId: IdZ, sourceId: z.string().nullable(), at: TsZ })),
    }),
  ),
});

export const HeadToHeadZ = z.object({
  a: IdZ,
  b: IdZ,
  oneVone: z.object({ w: z.number().int(), l: z.number().int(), pf: z.number().int(), pa: z.number().int() }),
  opposed: z.object({ w: z.number().int(), l: z.number().int() }),
  together: z.object({ w: z.number().int(), l: z.number().int() }),
  rivalry: z.boolean(),
  lastGames: z.array(z.object({ gameId: IdZ, playedAt: TsZ, winner: IdZ.nullable(), score: z.string() })),
});

export type RecordBookRes = z.infer<typeof RecordBookZ>;
export type HeadToHead = z.infer<typeof HeadToHeadZ>;

export const RivalryZ = z.object({
  a: IdZ,
  b: IdZ,
  n: z.number().int(),
  aWins: z.number().int(),
  score: z.number(),
});

export const getCard = defineRoute({
  name: 'stats.getCard',
  method: 'GET',
  path: '/api/stats/players/:id/card',
  auth: 'mem',
  res: RatingCardZ,
  errors: ['NOT_FOUND'],
});

export const listRatings = defineRoute({
  name: 'stats.listRatings',
  method: 'GET',
  path: '/api/stats/ratings',
  auth: 'mem',
  res: z.array(RatingCardZ),
  summary: 'Every player card: crew grid, matchmaking, Draft Night.',
});

export const ratingHistory = defineRoute({
  name: 'stats.ratingHistory',
  method: 'GET',
  path: '/api/stats/players/:id/ratings',
  auth: 'mem',
  res: RatingHistoryZ,
});

export const getDeltas = defineRoute({
  name: 'stats.getDeltas',
  method: 'GET',
  path: '/api/stats/deltas',
  auth: 'mem',
  query: z.object({ kind: DeltaSourceZ.shape.kind, id: IdZ }),
  res: z.array(RatingDeltaZ).nullable(),
  summary: 'null = not computed yet ("Ratings update when you get signal.").',
});

export const recompute = defineRoute({
  name: 'stats.recompute',
  method: 'POST',
  path: '/api/stats/recompute',
  auth: 'own',
  res: z.object({ engineVersion: z.number().int(), players: z.number().int(), ms: z.number() }),
});

export const playerStats = defineRoute({
  name: 'stats.player',
  method: 'GET',
  path: '/api/stats/players/:id',
  auth: 'mem',
  query: z.object({ window: WindowZ.optional() }),
  res: PlayerStatsZ,
});

export const playerGameLog = defineRoute({
  name: 'stats.playerGameLog',
  method: 'GET',
  path: '/api/stats/players/:id/games',
  auth: 'mem',
  query: z.object({ before: qInt(0, Number.MAX_SAFE_INTEGER).optional(), limit: qInt(1, 100).optional() }),
  res: z.object({
    items: z.array(PlayerLineZ.extend({ gameId: IdZ, playedAt: TsZ, mode: GameModeZ, score: z.string() })),
    nextBefore: TsZ.nullable(),
  }),
});

export const boxScore = defineRoute({
  name: 'stats.boxScore',
  method: 'GET',
  path: '/api/stats/games/:id/box',
  auth: 'mem',
  res: BoxScoreZ,
});

export const leaderboards = defineRoute({
  name: 'stats.leaderboards',
  method: 'GET',
  path: '/api/stats/leaderboards',
  auth: 'mem',
  query: z.object({ stat: z.string().min(1).max(40), window: WindowZ.optional(), limit: qInt(1, 50).optional() }),
  res: LeaderboardZ,
});

export const records = defineRoute({
  name: 'stats.records',
  method: 'GET',
  path: '/api/stats/records',
  auth: 'mem',
  res: RecordBookZ,
});

export const headToHead = defineRoute({
  name: 'stats.h2h',
  method: 'GET',
  path: '/api/stats/h2h',
  auth: 'mem',
  query: z.object({ a: IdZ, b: IdZ }),
  res: HeadToHeadZ,
});

const MatchupZ = z.object({ playerId: IdZ, w: z.number().int(), l: z.number().int() });

export const rivalries = defineRoute({
  name: 'stats.rivalries',
  method: 'GET',
  path: '/api/stats/rivalries',
  auth: 'mem',
  query: z.object({ playerId: IdZ.optional(), limit: qInt(1, 20).optional() }),
  res: z.object({
    items: z.array(RivalryZ),
    /** With `playerId`: who beats them most (lowest win rate, 3 games or more). */
    nemesis: MatchupZ.nullable(),
    /** With `playerId`: who they beat most. Optional so older clients can ignore it. */
    customer: MatchupZ.nullable().optional(),
  }),
});

export const H2HMatrixZ = z.object({
  /** Everyone who has played a 1v1, in a stable order. */
  players: z.array(IdZ),
  /** a's wins and losses against b in 1v1 games, both directions. */
  cells: z.array(z.object({ a: IdZ, b: IdZ, w: z.number().int(), l: z.number().int() })),
});

export type H2HMatrix = z.infer<typeof H2HMatrixZ>;

export const h2hMatrix = defineRoute({
  name: 'stats.h2hMatrix',
  method: 'GET',
  path: '/api/stats/h2h/matrix',
  auth: 'mem',
  res: H2HMatrixZ,
  summary: 'The 1v1 grid: every pair, both directions.',
});

export const badges = defineRoute({
  name: 'stats.badges',
  method: 'GET',
  path: '/api/stats/badges',
  auth: 'mem',
  query: z.object({ playerId: IdZ.optional() }),
  res: z.object({ items: z.array(BadgeEarnedZ) }),
});

export const feed = defineRoute({
  name: 'stats.feed',
  method: 'GET',
  path: '/api/stats/feed',
  auth: 'mem',
  query: z.object({
    before: qInt(0, Number.MAX_SAFE_INTEGER).optional(),
    /** With `before`: the id of the last row seen, so rows sharing one `at` are not skipped. */
    beforeId: z.string().min(1).max(64).optional(),
    limit: qInt(1, 50).optional(),
  }),
  res: z.object({
    items: z.array(FeedItemZ),
    nextBefore: TsZ.nullable(),
    /** Send back with `nextBefore`. Absent on old servers. */
    nextBeforeId: z.string().nullable().optional(),
  }),
  summary: 'Home refetches on focus; there is no feed stream in v1 (R14). Page with before + beforeId.',
});
